import TrackedVideo from "./TrackedVideo";
import { useGuide } from "../guides/GuideProvider";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { stripExtension } from "../utils/videoResolver";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

const normalizeText = (text) => text.replace(/^\uFEFF/, "").normalize("NFC").trim();
const segmentAliases = {
  "dubbel med skruv": "double with twist",
  "double with twist": "double with twist"
};

function canonicalizeSegment(segment) {
  const normalized = normalizeText(segment).toLowerCase();
  if (/^\d+$/.test(normalized)) {
    return String(parseInt(normalized, 10));
  }

  return segmentAliases[normalized] || normalized;
}

function pathsMatchWithAliases(leftPath, rightPath) {
  const leftSegments = leftPath.split("/").filter(Boolean).map(canonicalizeSegment);
  const rightSegments = rightPath.split("/").filter(Boolean).map(canonicalizeSegment);

  if (leftSegments.length !== rightSegments.length) {
    return false;
  }

  return leftSegments.every((segment, index) => segment === rightSegments[index]);
}

const Collapse = (props) => {
  const { report } = useGuide();
  const [open, setOpen] = useState(Boolean(props.initiallyOpen));
  const [url, setUrl] = useState("");
  const [fallbackTitle, setFallbackTitle] = useState("");
  const navigate = useNavigate();

  const handleClick = (title) => {
    props.setExercise({
      ...props.exercise,
      videoPath: `${props.path}/${title}`,
    });
    report("video-selected");
    navigate(-1);
  };

  const searchPath = normalizeText(`${props.path}/${stripExtension(props.title)}`);
  const metadataEntries = Object.entries(props.videoMetadata || {});
  const exactMatch = props.videoMetadata?.[searchPath];
  const aliasMatch = !exactMatch
    ? metadataEntries.find(([clipPath]) => pathsMatchWithAliases(clipPath, searchPath))?.[1]
    : null;
  const clipMetadata = exactMatch || aliasMatch || {};
  const displayTitle = props.displayTitle || clipMetadata.title || fallbackTitle || searchPath;
  const displayDescription = props.description || clipMetadata.description || "Utförlig beskrivning saknas.";

  useEffect(() => {
    const fetchFallbackTitle = async () => {
      try {
        const response = await fetch("/titles.csv");
        const text = normalizeText(await response.text());
        const rows = text
          .split(/\r?\n/)
          .map((row) => row.trim())
          .filter(Boolean)
          .map((row) => {
            const [path, ...titleParts] = row.split(",");
            return [normalizeText(path || ""), normalizeText(titleParts.join(","))];
          });

        const matchingRow = rows.find((row) => row[0] === searchPath)
          || rows.find((row) => pathsMatchWithAliases(row[0], searchPath));

        setFallbackTitle(matchingRow && matchingRow[1] ? matchingRow[1].trim() : searchPath);
      } catch (error) {
        console.error("Error reading fallback CSV:", error);
        setFallbackTitle(searchPath);
      }
    };

    fetchFallbackTitle();
  }, [searchPath]);

  useEffect(() => {
    if (open && url === "") {
      const fetchUrl = async () => {
        try {
          const response = await axios.get(`${API_URL}/bucket/presigned-url-file`, {
            headers: {
              "Content-Type": "application/json",
              token: localStorage.getItem("token"),
            },
            params: {
              file: encodeURIComponent(`${props.path.slice(1)}/${props.title}`),
            },
          });
          setUrl(response.data);
        } catch (err) {
          console.error("Error fetching presigned URL:", err);
        }
      };

      fetchUrl();
    }
  }, [open, url, props.path, props.title]);

  return (
    <article className="my-3 rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 px-3 py-3 shadow-sm sm:my-4 sm:rounded-[24px] sm:px-5 sm:py-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="min-w-0 flex-1 text-left text-base font-semibold text-slate-800 hover:text-sky-700 sm:text-lg"
          data-guide="video-open"
          onClick={() => { setOpen(!open); if (!open) report("video-opened"); }}
          aria-expanded={open}
        >
          <span className="block break-words">{displayTitle}</span>
          {props.folderLabel && <span className="mt-1 block truncate text-xs font-normal text-slate-500">{props.folderLabel}</span>}
        </button>
        {props.onToggleFavorite && (
          <button
            type="button"
            className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-xl shadow-sm transition-colors disabled:cursor-wait disabled:opacity-60 ${props.isFavorite ? "border-amber-300 bg-amber-50 text-amber-600" : "border-slate-200 bg-white text-slate-400 hover:text-amber-500"}`}
            data-guide="video-favorite"
            onClick={() => props.onToggleFavorite()}
            disabled={props.favoriteDisabled}
            aria-label={props.isFavorite ? `Ta bort ${displayTitle} från favoriter` : `Lägg till ${displayTitle} som favorit`}
            aria-pressed={props.isFavorite}
            title={props.isFavorite ? "Ta bort från favoriter" : "Lägg till som favorit"}
          >
            <span aria-hidden="true">{props.isFavorite ? "★" : "☆"}</span>
          </button>
        )}
        {props.state && (
          <button
            type="button"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-sky-700 to-cyan-500 text-2xl leading-none text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
            onClick={(e) => {
              e.stopPropagation();
              handleClick(props.title);
            }}
            data-guide="video-picker"
            aria-label={`Välj ${displayTitle}`}
          >
            +
          </button>
        )}
      </div>
      {open && (
        <>
          <div className="mt-2 text-sm text-slate-600">
            {displayDescription}
          </div>
          {url && (
            <div className="flex justify-center py-2">
              <TrackedVideo clipPath={searchPath} clipTitle={displayTitle} controls key={props.title} className="max-h-[65vh] w-full rounded-2xl bg-black shadow-sm">
                <source src={url} />
              </TrackedVideo>
            </div>
          )}
        </>
      )}
    </article>
  );
};

export default Collapse;
